import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toPng } from "html-to-image";
import {
  Play,
  Pause,
  DocumentDownload,
  LanguageCircle,
  Maximize4,
  CloseCircle,
  Magicpen,
} from "iconsax-react";

import MobileLayout from "../../../components/MobileLayout";
import geminiService from "../../../services/geminiService";

import {
  PageScroll,
  WordHeaderCard,
  WordHeaderTitle,
  WordHeaderThumb,
  WordHeaderLetterFallback,
  CaptureBox,
  CaptureInner,
  HeroLetterWrap,
  FullscreenCornerBtn,
  BigLibras,
  BigLatin,
  PerWordSection,
  PerWordCard,
  PerWordLabel,
  LettersRow,
  LetterTile,
  SpeedBlock,
  SpeedLabelRow,
  SpeedChips,
  SpeedChip,
  PrimaryAction,
  GlosaPanel,
  GlosaHighlight,
  InfoPanel,
  ModalBackdrop,
  ModalTopBar,
  ModalTitle,
  ModalClose,
  ModalStage,
  ModalLetterBox,
  ModalBigLibras,
  ModalBigLatin,
  ImageModalImg,
  EmptyHint,
} from "./styles";

function normalizeForLibrasFont(text: string): string {
  if (!text || typeof text !== "string") return "";
  const withoutAccents = text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const withoutNumbers = withoutAccents.replace(/\d/g, "");
  return withoutNumbers.replace(/[^A-Za-z]/g, "").toUpperCase();
}

type LocState = { word?: string; imageUri?: string };

function Dactylology() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { word = "", imageUri } = (state as LocState) || {};

  const [currentLetterIndex, setCurrentLetterIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speedMs, setSpeedMs] = useState(1500);
  const [initialLoad, setInitialLoad] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [imageModal, setImageModal] = useState(false);
  const [glosa, setGlosa] = useState<string | null>(null);
  const [glosaRule, setGlosaRule] = useState<string | null>(null);
  const [glosaExplanation, setGlosaExplanation] = useState<string | null>(null);
  const [loadingGlosa, setLoadingGlosa] = useState(false);
  const [savingImage, setSavingImage] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [glosaError, setGlosaError] = useState<string | null>(null);

  const timeoutRefs = useRef<ReturnType<typeof setTimeout>[]>([]);
  const captureRef = useRef<HTMLDivElement>(null);

  const { wordsNormalized, allCharacters, wordStartIndexes } = useMemo(() => {
    const rawWords = (word || "").trim().split(/\s+/).filter(Boolean);
    const words =
      rawWords.length > 0
        ? rawWords
            .map((w) => normalizeForLibrasFont(w))
            .filter((w) => w.length > 0)
        : [normalizeForLibrasFont(word || "")].filter((w) => w.length > 0);
    const all = words.flatMap((w) => w.split(""));
    const starts: number[] = [];
    let idx = 0;
    words.forEach((w) => {
      starts.push(idx);
      idx += w.length;
    });
    return {
      wordsNormalized: words,
      allCharacters: all,
      wordStartIndexes: starts,
    };
  }, [word]);

  useEffect(() => {
    setCurrentLetterIndex(0);
    setIsPlaying(false);
    timeoutRefs.current.forEach(clearTimeout);
    timeoutRefs.current = [];
    const t = setTimeout(() => setInitialLoad(false), 450);
    return () => {
      clearTimeout(t);
      timeoutRefs.current.forEach(clearTimeout);
    };
  }, [word]);

  const clearTimers = useCallback(() => {
    timeoutRefs.current.forEach(clearTimeout);
    timeoutRefs.current = [];
  }, []);

  const playLetterByLetter = useCallback(() => {
    if (isPlaying) {
      clearTimers();
      setIsPlaying(false);
      setCurrentLetterIndex(0);
      return;
    }

    const chars = allCharacters;
    if (chars.length === 0) return;

    clearTimers();
    setIsPlaying(true);
    setCurrentLetterIndex(0);

    chars.forEach((_, index) => {
      const t = setTimeout(() => {
        setCurrentLetterIndex(index);
        if (index === chars.length - 1) {
          const t2 = setTimeout(() => {
            setIsPlaying(false);
            setCurrentLetterIndex(0);
          }, speedMs);
          timeoutRefs.current.push(t2);
        }
      }, index * speedMs);
      timeoutRefs.current.push(t);
    });
  }, [allCharacters, clearTimers, isPlaying, speedMs]);

  const displayLibrasChar = isPlaying
    ? allCharacters[currentLetterIndex]
    : allCharacters[0];
  const displayLatinChar = displayLibrasChar;
  const hasLibrasFont = !!displayLibrasChar && /[A-Z]/.test(displayLibrasChar);

  const handleSavePng = async () => {
    if (!captureRef.current || savingImage) return;
    setSaveError(null);
    setSavingImage(true);
    try {
      const dataUrl = await toPng(captureRef.current, {
        cacheBust: true,
        pixelRatio: 2,
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `datilologia-${
        normalizeForLibrasFont(word) || "palavra"
      }.png`;
      a.click();
    } catch {
      setSaveError("Não foi possível gerar a imagem. Tente outro navegador.");
    } finally {
      setSavingImage(false);
    }
  };

  const handleGlosa = async () => {
    if (!word?.trim() || loadingGlosa) return;
    setLoadingGlosa(true);
    setGlosa(null);
    setGlosaRule(null);
    setGlosaExplanation(null);
    setGlosaError(null);
    try {
      const result = await geminiService.generateGlosaForWord(word);
      const glosaText = String(result.glosa || "")
        .replace(/^GLOSA:\s*/i, "")
        .trim();
      setGlosa(glosaText);
      setGlosaRule(result.rule || null);
      setGlosaExplanation(result.explanation || null);
    } catch {
      setGlosa(null);
      setGlosaError(
        "Falha ao traduzir glosa. Verifique REACT_APP_GEMINI_API_KEY."
      );
    } finally {
      setLoadingGlosa(false);
    }
  };

  const speedOptions: { label: string; ms: number }[] = [
    { label: "Lento", ms: 2500 },
    { label: "Normal", ms: 1500 },
    { label: "Rápido", ms: 800 },
    { label: "Muito rápido", ms: 500 },
  ];

  if (!word) {
    return (
      <MobileLayout headerTitle="Datilologia" headerType="private">
        <PageScroll>
          <EmptyHint>
            Nenhuma palavra informada. Abra um verbete no sinalário e use “Ver
            datilologia”.
          </EmptyHint>
          <PrimaryAction type="button" onClick={() => navigate("/sinalario")}>
            Ir ao sinalário
          </PrimaryAction>
        </PageScroll>
      </MobileLayout>
    );
  }

  if (initialLoad) {
    return (
      <MobileLayout headerTitle="Datilologia" headerType="private">
        <PageScroll>
          <p style={{ textAlign: "center" }}>Carregando…</p>
        </PageScroll>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout headerTitle="Datilologia" headerType="private">
      <PageScroll>
        <WordHeaderCard>
          <WordHeaderTitle>{word}</WordHeaderTitle>
          {(() => {
            if (imageUri) {
              return (
                <WordHeaderThumb
                  type="button"
                  aria-label="Ver imagem do sinal em tela cheia"
                  onClick={() => setImageModal(true)}
                >
                  <img src={imageUri} alt="" />
                </WordHeaderThumb>
              );
            }
            if (allCharacters.length > 0) {
              return (
                <WordHeaderLetterFallback>
                  <span>{allCharacters[0]}</span>
                </WordHeaderLetterFallback>
              );
            }
            return null;
          })()}
        </WordHeaderCard>

        <CaptureBox ref={captureRef}>
          <CaptureInner>
            {allCharacters.length === 0 ? (
              <p style={{ textAlign: "center", color: "#64748b" }}>
                Nenhuma letra latina encontrada para esta palavra (após remover
                acentos e números).
              </p>
            ) : (
              <>
                <HeroLetterWrap>
                  <button
                    type="button"
                    onClick={() => setFullscreen(true)}
                    style={{
                      border: "none",
                      background: "transparent",
                      padding: 0,
                      width: "100%",
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <BigLibras $hasFont={hasLibrasFont}>
                      {displayLibrasChar || ""}
                    </BigLibras>
                    {displayLatinChar ? (
                      <BigLatin>{displayLatinChar}</BigLatin>
                    ) : null}
                  </button>
                  <FullscreenCornerBtn
                    type="button"
                    aria-label="Tela cheia"
                    onClick={() => setFullscreen(true)}
                  >
                    <Maximize4 size={22} color="#fff" variant="Bold" />
                  </FullscreenCornerBtn>
                </HeroLetterWrap>

                <PerWordSection>
                  {wordsNormalized.map((wordStr, wordIdx) => {
                    const startIdx = wordStartIndexes[wordIdx];
                    const wordChars = wordStr.split("");
                    return (
                      <PerWordCard key={`${wordStr}-s${startIdx}`}>
                        <PerWordLabel>{wordStr}</PerWordLabel>
                        <LettersRow>
                          {wordChars.map((char, letterIdx) => {
                            const globalIndex = startIdx + letterIdx;
                            const active =
                              isPlaying && globalIndex === currentLetterIndex;
                            const soft =
                              globalIndex <= currentLetterIndex && !active;
                            return (
                              <LetterTile
                                key={`${wordStr}-g${globalIndex}-${char}`}
                                $active={active}
                                $soft={soft}
                              >
                                <span className="lib">{char}</span>
                                <span className="lat">{char}</span>
                              </LetterTile>
                            );
                          })}
                        </LettersRow>
                      </PerWordCard>
                    );
                  })}
                </PerWordSection>

                <SpeedBlock>
                  <SpeedLabelRow>
                    <Magicpen size={22} color="#5F33E1" variant="Bold" />
                    Velocidade do sinal
                  </SpeedLabelRow>
                  <SpeedChips>
                    {speedOptions.map((opt) => (
                      <SpeedChip
                        key={opt.ms}
                        type="button"
                        $active={speedMs === opt.ms}
                        disabled={isPlaying}
                        onClick={() => setSpeedMs(opt.ms)}
                      >
                        {opt.label}
                      </SpeedChip>
                    ))}
                  </SpeedChips>
                </SpeedBlock>
              </>
            )}
          </CaptureInner>
        </CaptureBox>

        <PrimaryAction type="button" onClick={playLetterByLetter}>
          {isPlaying ? (
            <Pause size={26} variant="Bold" />
          ) : (
            <Play size={26} variant="Bold" />
          )}
          {isPlaying ? "Pausar soletração" : "Reproduzir soletração"}
        </PrimaryAction>

        <PrimaryAction
          type="button"
          onClick={handleSavePng}
          disabled={savingImage || allCharacters.length === 0}
        >
          <DocumentDownload size={26} variant="Bold" />
          {savingImage ? "Gerando PNG…" : "Salvar imagem (PNG)"}
        </PrimaryAction>
        {saveError ? (
          <p style={{ color: "#ef4444", fontSize: "14px", margin: 0 }}>
            {saveError}
          </p>
        ) : null}

        <PrimaryAction
          type="button"
          onClick={handleGlosa}
          disabled={loadingGlosa}
        >
          <LanguageCircle size={26} variant="Bold" />
          {loadingGlosa ? "Traduzindo…" : "Traduzir glosa (IA)"}
        </PrimaryAction>
        {glosaError ? (
          <p style={{ color: "#ef4444", fontSize: "14px", margin: 0 }}>
            {glosaError}
          </p>
        ) : null}

        {glosa ? (
          <GlosaPanel>
            <div style={{ fontWeight: 700, color: "#1e293b" }}>
              Glosa de “{word}”
            </div>
            <GlosaHighlight>{glosa}</GlosaHighlight>
            <p style={{ margin: 0, fontSize: "14px", color: "#64748b" }}>
              A glosa é a representação escrita da estrutura da Libras.
            </p>
            {glosaRule ? (
              <div>
                <strong style={{ color: "#5F33E1" }}>Regra gramatical</strong>
                <p style={{ margin: "0.4rem 0 0", color: "#334155" }}>
                  {glosaRule}
                </p>
              </div>
            ) : null}
            {glosaExplanation ? (
              <div>
                <strong style={{ color: "#5F33E1" }}>Explicação</strong>
                <p style={{ margin: "0.4rem 0 0", color: "#64748b" }}>
                  {glosaExplanation}
                </p>
              </div>
            ) : null}
          </GlosaPanel>
        ) : null}

        <InfoPanel>
          <strong style={{ color: "#1e293b" }}>O que é datilologia?</strong>
          <p style={{ margin: "0.6rem 0 0" }}>
            A datilologia é a soletração manual em Libras: cada letra do
            alfabeto tem uma configuração da mão. A palavra “{word}” é exibida
            letra a letra com a fonte Libras2020 (quando disponível no
            navegador).
          </p>
        </InfoPanel>

        <PrimaryAction type="button" onClick={() => navigate("/sinalario")}>
          Alfabeto e sinalário
        </PrimaryAction>
      </PageScroll>

      {fullscreen && allCharacters.length > 0 ? (
        <ModalBackdrop>
          <ModalTopBar>
            <ModalTitle>{word}</ModalTitle>
            <ModalClose
              type="button"
              aria-label="Fechar"
              onClick={() => setFullscreen(false)}
            >
              <CloseCircle size={28} color="#fff" variant="Bold" />
            </ModalClose>
          </ModalTopBar>
          <ModalStage>
            <ModalLetterBox>
              <ModalBigLibras $hasFont={hasLibrasFont}>
                {displayLibrasChar || ""}
              </ModalBigLibras>
              {displayLatinChar ? (
                <ModalBigLatin>{displayLatinChar}</ModalBigLatin>
              ) : null}
            </ModalLetterBox>
          </ModalStage>
        </ModalBackdrop>
      ) : null}

      {imageUri && imageModal ? (
        <ModalBackdrop>
          <ModalTopBar>
            <ModalTitle>{word}</ModalTitle>
            <ModalClose
              type="button"
              aria-label="Fechar"
              onClick={() => setImageModal(false)}
            >
              <CloseCircle size={28} color="#fff" variant="Bold" />
            </ModalClose>
          </ModalTopBar>
          <ModalStage>
            <ImageModalImg src={imageUri} alt="" />
          </ModalStage>
        </ModalBackdrop>
      ) : null}
    </MobileLayout>
  );
}

export default Dactylology;
